import type { TFunction } from 'i18next';

import type { GivingHistoryItem } from '@sola/schema/dotcom/proxy-api/givingHistory.ts';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';
import type {
  GraphQLCommitment,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import type { GivingItemWithCommitment } from '../../utils/giving-history.ts';

export type GroupedGivingHistoryItems = Record<
  string,
  GivingItemWithCommitment[]
>;

export enum MonthNamesEnum {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December',
}

export enum MonthEnum {
  January,
  February,
  March,
  April,
  May,
  June,
  July,
  August,
  September,
  October,
  November,
  December,
}

const mapMonthNumToName = (num: MonthEnum): MonthNamesEnum | null => {
  switch (num) {
    case MonthEnum.January: {
      return MonthNamesEnum.January;
    }
    case MonthEnum.February: {
      return MonthNamesEnum.February;
    }
    case MonthEnum.March: {
      return MonthNamesEnum.March;
    }
    case MonthEnum.April: {
      return MonthNamesEnum.April;
    }
    case MonthEnum.May: {
      return MonthNamesEnum.May;
    }
    case MonthEnum.June: {
      return MonthNamesEnum.June;
    }
    case MonthEnum.July: {
      return MonthNamesEnum.July;
    }
    case MonthEnum.August: {
      return MonthNamesEnum.August;
    }
    case MonthEnum.September: {
      return MonthNamesEnum.September;
    }
    case MonthEnum.October: {
      return MonthNamesEnum.October;
    }
    case MonthEnum.November: {
      return MonthNamesEnum.November;
    }
    case MonthEnum.December: {
      return MonthNamesEnum.December;
    }
    default: {
      return null;
    }
  }
};

export const mapFundCodeToCommitmentType = (fundCode: string | null) => {
  switch (fundCode) {
    case CommitmentCodeEnum.ChildSponsorship: {
      return GraphQLCommitmentType.ChildSponsorship;
    }
    case CommitmentCodeEnum.BirthdayGift:
    case CommitmentCodeEnum.ChildGift:
    case CommitmentCodeEnum.FamilyGift:
    case CommitmentCodeEnum.CenterGift: {
      return GraphQLCommitmentType.GiftCommitment;
    }
  }
};

const mapNumToMonthNum = (num: number | string): MonthEnum => {
  switch (Number(num)) {
    case 0: {
      return MonthEnum.January;
    }
    case 1: {
      return MonthEnum.February;
    }
    case 2: {
      return MonthEnum.March;
    }
    case 3: {
      return MonthEnum.April;
    }
    case 4: {
      return MonthEnum.May;
    }
    case 5: {
      return MonthEnum.June;
    }
    case 6: {
      return MonthEnum.July;
    }
    case 7: {
      return MonthEnum.August;
    }
    case 8: {
      return MonthEnum.September;
    }
    case 9: {
      return MonthEnum.October;
    }
    case 10: {
      return MonthEnum.November;
    }
    case 11: {
      return MonthEnum.December;
    }
    default: {
      return MonthEnum.January;
    }
  }
};

/**
 * Returns an array of descending years of length `count`
 */
export const getYearList = (now: Date, count: number): number[] => {
  const yearList: number[] = [now.getFullYear()];

  while (yearList.length < count) {
    // The suggested fix from unicorn resulted in a failed compilation...
    // eslint-disable-next-line unicorn/prefer-at
    const lastYear = yearList[yearList.length - 1];
    yearList.push(lastYear - 1);
  }

  return yearList;
};

const attachCommitmentToGivingItem =
  (
    commitments?: Map<string, GraphQLCommitment>,
    pledges?: Map<string, GraphQLPledge>,
  ) =>
  (givingItem: GivingHistoryItem): GivingItemWithCommitment => {
    return {
      ...givingItem,
      ...(givingItem.commitmentId &&
        commitments?.has(givingItem.commitmentId) && {
          commitment: commitments.get(givingItem.commitmentId),
        }),
      ...(givingItem.pledgeId &&
        pledges?.has(givingItem.pledgeId) && {
          pledge: pledges.get(givingItem.pledgeId),
        }),
    };
  };

export const getGivingHistoryTotal = (
  givingHistoryItems: GivingHistoryItem[],
) =>
  givingHistoryItems.reduce((acc, givingHistoryItem) => {
    if (!givingHistoryItem?.amount) {
      return acc;
    }
    return givingHistoryItem.isRefund
      ? acc - givingHistoryItem.amount
      : acc + givingHistoryItem.amount;
  }, 0);

export const groupGivingItemsByMonth = (
  givingHistoryItems: GivingHistoryItem[],
  commitments?: GraphQLCommitment[],
  pledges?: GraphQLPledge[],
) => {
  const initialValue: Record<number, GivingItemWithCommitment[]> = {
    [MonthEnum.January]: [],
    [MonthEnum.February]: [],
    [MonthEnum.March]: [],
    [MonthEnum.April]: [],
    [MonthEnum.May]: [],
    [MonthEnum.June]: [],
    [MonthEnum.July]: [],
    [MonthEnum.August]: [],
    [MonthEnum.September]: [],
    [MonthEnum.October]: [],
    [MonthEnum.November]: [],
    [MonthEnum.December]: [],
  };

  // Create the lookup once to allow easy finding/attaching of the commitment
  const commitmentMap = new Map(
    commitments?.map((comm) => [comm.globalId, comm]),
  );
  const pledgeMap = new Map(
    pledges?.map((pledge) => [pledge.pledgeId, pledge]),
  );

  return givingHistoryItems.reduce((acc, givingHistoryItem) => {
    const dateString = givingHistoryItem.recordDate;
    const addCommitment = attachCommitmentToGivingItem(
      commitmentMap,
      pledgeMap,
    );
    if (dateString) {
      const date = new Date(dateString);
      const monthNum = date.getUTCMonth();
      acc[monthNum] = [...acc[monthNum], addCommitment(givingHistoryItem)];
    }
    return acc;
  }, initialValue);
};

export const getHistoryItemsByMonth = (
  year: number,
  historyItems: GivingHistoryItem[],
  commitments: GraphQLCommitment[] | undefined,
  pledges: GraphQLPledge[] | undefined,
  t: TFunction<'my', undefined>,
) => {
  const group = groupGivingItemsByMonth(historyItems, commitments, pledges);

  return Object.entries(group)
    .map(([monthNum, givingHistoryItems]) => ({
      heading: t('givingHistory.total', {
        month: mapMonthNumToName(mapNumToMonthNum(monthNum)),
        year,
      }),
      givingHistoryItems,
    }))
    .toReversed()
    .filter((month) => month.givingHistoryItems.length);
};

const dateOptions: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
};

export const getGroupedHistoryItems = (
  historyItems?: GivingItemWithCommitment[],
) => {
  if (!historyItems?.length) {
    return [];
  }

  const dateGroups = historyItems.reduce((acc, item) => {
    if (item.recordDate) {
      acc[item.recordDate] =
        item.recordDate in acc ? [...acc[item.recordDate], item] : [item];
    }
    return acc;
  }, {} as GroupedGivingHistoryItems);

  return Object.entries(dateGroups)
    .map(([key, value]) => {
      const date = new Date(key);
      const formattedDate = date.toLocaleDateString('en-US', dateOptions);

      return {
        heading: formattedDate,
        historyItems: value,
      };
    })
    .toReversed();
};
